import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import classNames from 'classnames';
import { t } from 'i18next';
import DragAndDrop from './dragAndDrop/DragAndDrop';
import styles from './TextAreaWithSearch.module.scss';
const TextAreaWithSearch = ({ items, selected, onChangeSelected, error, onSelect, onUnselect, placeholder, disabled, className, hasInsideLabel, insideLabel, allowEmpty, maxLimit = 0, showListOnClick = false, showListAlways = false, emptyText = t('label.not_found'), isSearchableByAll, isSearchableByAliases, unsupportedItems, }) => {
    const [value, setValue] = useState('');
    const ref = useRef();
    const refList = useRef();
    const [isShowSearch, setShowSearch] = useState(false);
    const [localData, setLocalData] = useState(items);
    const [filteredList, setFilteredList] = useState(items.filter((el) => !selected.includes(el.id)));
    const [selectedItems, setSelectedItems] = useState(items.filter((el) => selected.includes(el.id)));
    const [focusedItemIndex, setFocusedItemIndex] = useState(-1);
    const [focusedSearchItemIndex, setFocusedSearchItemIndex] = useState(-1);
    const [selectedCount, setSelectedCount] = useState(selected.length);
    const [isActive, setActive] = useState(false);
    const isSelectingAvailable = maxLimit > 0 ? selected.length < maxLimit : localData.length;
    useEffect(() => {
        const unselectedItems = items.filter((el) => !selected.includes(el.id));
        setFilteredList(unselectedItems);
        setLocalData(unselectedItems);
        const selectedItems = [];
        for (const id of selected) {
            const item = items.find((item) => item.id === id);
            if (item)
                selectedItems.push(item);
        }
        setSelectedItems(selectedItems);
        setSelectedCount(selectedItems.length);
    }, [items, selected]);
    useEffect(() => {
        setSelectedCount(selectedItems.filter((el) => selected.includes(el.id)).length);
    }, [selectedItems]);
    useEffect(() => {
        if (value) {
            setShowSearch(true);
            setFilteredList(localData.filter((el) => {
                var _a;
                const query = value.toLowerCase().trim();
                if (isSearchableByAliases) {
                    return (el.value.toLowerCase().includes(query) ||
                        el.id.toLowerCase().includes(query) ||
                        ((_a = el.aliases) === null || _a === void 0 ? void 0 : _a.some((alias) => alias.toLowerCase().includes(query))));
                }
                if (isSearchableByAll) {
                    return el.value.toLowerCase().includes(query) || el.id.toLowerCase().includes(query);
                }
                return el.value.toLowerCase().includes(query);
            }));
        }
        else if (showListAlways && isActive) {
            setShowSearch(true);
            setFilteredList([...localData]);
        }
        else {
            setShowSearch(false);
        }
        setFocusedSearchItemIndex(0);
    }, [localData, value]);
    useEffect(() => {
        if (!refList.current)
            return;
        const activeElement = refList.current.querySelector('.' + styles.listElemActive);
        if (activeElement) {
            activeElement.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'nearest' });
        }
    }, [focusedSearchItemIndex, refList]);
    useEffect(() => {
        if (!isActive && isShowSearch) {
            setShowSearch(false);
            setFocusedItemIndex(-1);
        }
        else if (isActive && !isShowSearch) {
            setShowSearch(showListAlways);
        }
    }, [isActive]);
    const handleKeyDown = (event) => {
        if (isShowSearch) {
            switch (event.key) {
                case 'ArrowUp':
                    event.preventDefault();
                    if (focusedSearchItemIndex > 0) {
                        setFocusedSearchItemIndex(focusedSearchItemIndex - 1);
                    }
                    break;
                case 'ArrowDown':
                    event.preventDefault();
                    if (focusedSearchItemIndex < filteredList.length - 1) {
                        setFocusedSearchItemIndex(focusedSearchItemIndex + 1);
                    }
                    break;
                case 'Enter':
                    event.preventDefault();
                    filteredList.length &&
                        selectItem(event, filteredList.find((e, i) => i === focusedSearchItemIndex));
                    break;
                case 'Escape':
                case 'Backspace':
                case 'Delete':
                    if (!value) {
                        setShowSearch(showListAlways);
                    }
                    break;
                default:
                    break;
            }
        }
        if (!isShowSearch || (showListAlways && !value)) {
            switch (event.key) {
                case 'Backspace':
                case 'Delete':
                    if (selectedItems.length) {
                        setFocusedItemIndex(selectedItems.length - 1);
                    }
                    if (focusedItemIndex > -1) {
                        removeItem(event, selectedItems[focusedItemIndex]);
                        if (focusedItemIndex < selectedItems.length - 1) {
                            setFocusedItemIndex(focusedItemIndex);
                        }
                        else {
                            setFocusedItemIndex(-1);
                        }
                    }
                    break;
                case 'ArrowLeft':
                    if (focusedItemIndex === -1) {
                        setFocusedItemIndex(selectedItems.length - 1);
                    }
                    else if (focusedItemIndex !== 0) {
                        setFocusedItemIndex(focusedItemIndex - 1);
                    }
                    break;
                case 'ArrowRight':
                    if (focusedItemIndex === selectedItems.length - 1 || focusedItemIndex === -1) {
                        setFocusedItemIndex(-1);
                    }
                    else {
                        setFocusedItemIndex(focusedItemIndex + 1);
                    }
                    break;
                default:
                    setFocusedItemIndex(-1);
            }
        }
    };
    const selectItem = (e, selectedItem) => {
        e.preventDefault();
        e.stopPropagation();
        onSelect(selectedItem);
        const notSelectedItems = localData.filter((item) => item.id !== selectedItem.id);
        setLocalData(notSelectedItems);
        setFilteredList(notSelectedItems);
        setSelectedItems((prevState) => [...prevState, selectedItem]);
        setValue('');
    };
    const removeItem = (e, itemRm) => {
        e.preventDefault();
        e.stopPropagation();
        onUnselect(itemRm);
        setSelectedItems(selectedItems.filter((item) => item.id !== itemRm.id));
        setFilteredList((prev) => [...prev, itemRm]);
        setLocalData((localData) => [...localData, itemRm]);
    };
    const handleClickTextArea = (e) => {
        e.stopPropagation();
        if (!isShowSearch && (showListOnClick || showListAlways)) {
            setShowSearch(true);
        }
        setActive(true);
        ref.current.focus();
    };
    useEffect(() => {
        if (!isSelectingAvailable) {
            setActive(false);
        }
    }, [isSelectingAvailable]);
    const onChange = (event) => {
        setValue(event.target.value);
    };
    const moveSelected = useCallback((dragIndex, hoverIndex) => {
        const newSelected = [...selected];
        const dragItem = newSelected.splice(dragIndex, 1);
        const leftSide = newSelected.slice(0, hoverIndex);
        const rightSide = newSelected.slice(hoverIndex);
        onChangeSelected([...leftSide, dragItem[0], ...rightSide]);
    }, [selected, onChangeSelected]);
    return (_jsxs(_Fragment, { children: [_jsxs("div", Object.assign({ className: classNames(styles.textArea, className, isSelectingAvailable && styles.textAreaAvailable, error && styles.textAreaInputError, disabled && styles.textAreaDisabled, isActive && isSelectingAvailable && styles.textAreaActive), onClick: isSelectingAvailable ? handleClickTextArea : undefined }, { children: [maxLimit > 0 ? (_jsxs("div", Object.assign({ className: styles.textAreaCounter }, { children: [selectedCount, "/", maxLimit] }))) : null, hasInsideLabel && insideLabel && (_jsx("label", Object.assign({ className: styles.insideLabel }, { children: insideLabel }))), _jsxs("div", Object.assign({ className: styles.textAreaContent }, { children: [_jsx(DragAndDrop, { selectedItems: selectedItems, selectedCount: selectedCount, removeItem: removeItem, moveSelected: moveSelected, unsupportedItems: unsupportedItems, focusedItemIndex: focusedItemIndex, isSearchableByAll: isSearchableByAll, isDisabled: disabled, allowEmpty: allowEmpty }), isSelectingAvailable ? (_jsxs("div", Object.assign({ className: styles.textAreaInputContainer }, { children: [_jsx("input", { value: value, onChange: onChange, onBlur: () => setActive(false), onFocus: () => setActive(true), onKeyDown: handleKeyDown, placeholder: placeholder, ref: ref, autoComplete: "off", disabled: disabled, className: styles.textAreaInput }), isShowSearch && !disabled && (_jsx("div", Object.assign({ className: classNames(styles.list), ref: refList }, { children: _jsx("div", Object.assign({ className: classNames(styles.listScroll, 'styled_scroll') }, { children: filteredList.length ? (filteredList.map((el, index) => (_jsx("div", Object.assign({ className: classNames(styles.listElem, index === focusedSearchItemIndex && styles.listElemActive), onMouseOver: () => setFocusedSearchItemIndex(index), onMouseDown: (e) => selectItem(e, el) }, { children: _jsxs("span", Object.assign({ className: styles.listElemLabel }, { children: [el.value, el.secondaryValue ? (_jsxs("span", { children: ["\u00A0\u2022\u00A0", el.secondaryValue] })) : null] })) }), el.id)))) : (_jsx("div", Object.assign({ className: styles.listEmpty }, { children: emptyText }))) })) })))] }))) : null] }))] })), !!(error === null || error === void 0 ? void 0 : error.length) && _jsx("div", Object.assign({ className: styles.error }, { children: error }))] }));
};
export default observer(TextAreaWithSearch);
